import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { PlatformGrowthData } from '../../services/api/artist-analytics.service';
import {
  getCategoryAxisLayout,
  splitParentheticalCategoryLabel,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface WeeklyGrowthChartProps {
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const WeeklyGrowthChart: React.FC<WeeklyGrowthChartProps> = ({
  artistId,
  title = 'Weekly Growth',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<PlatformGrowthData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await artistAnalyticsService.getPlatformGrowthData(artistId);
        setData(response.data);
      } catch (err) {
        setError('Failed to load weekly growth data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, refreshKey]);

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    // Filter and sort data by weekly growth
    const weeklyData = data
      .filter(item => item.weekly_growth !== 0)
      .sort((a, b) => b.weekly_growth - a.weekly_growth);

    const platforms = weeklyData.map(item => `${item.platform} (${item.metric_type})`);
    const growthValues = weeklyData.map(item => item.weekly_growth);
    const growthRates = weeklyData.map(item => item.weekly_growth_rate);
    const xLayout = getCategoryAxisLayout(platforms.length, 'longLabels');

    // Color coding: positive green, negative red
    const colors = growthValues.map(value => value >= 0 ? '#10B981' : '#EF4444');

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const item = weeklyData[dataIndex];
          const growth = item.weekly_growth;
          const rate = item.weekly_growth_rate;
          const velocity = item.daily_growth_velocity;
          
          return `
            <div style="font-weight: 600; margin-bottom: 8px; color: #1f2937;">${item.platform} (${item.metric_type})</div>
            <div style="color: ${growth >= 0 ? '#10B981' : '#EF4444'}; margin-bottom: 4px;">
              ● Weekly Growth: ${growth >= 0 ? '+' : ''}${growth.toLocaleString()}
            </div>
            <div style="color: #6b7280; font-size: 11px; margin-bottom: 4px;">
              Growth Rate: ${rate >= 0 ? '+' : ''}${rate.toFixed(2)}%
            </div>
            <div style="color: #6b7280; font-size: 11px;">
              Daily Velocity: ${velocity >= 0 ? '+' : ''}${velocity.toFixed(1)}/day
            </div>
          `;
        }
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '15%',
        bottom: xLayout.gridBottom,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: platforms,
        axisLine: {
          lineStyle: {
            color: '#d1d5db'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 14,
          formatter: (value: string) => splitParentheticalCategoryLabel(value)
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: 10,
          formatter: function(value: number) {
            if (Math.abs(value) >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (Math.abs(value) >= 1000) {
              return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toString();
          }
        },
        splitLine: {
          lineStyle: {
            color: '#e5e7eb',
            width: 1
          }
        },
        splitNumber: 4
      },
      series: [
        {
          name: 'Weekly Growth',
          type: 'bar',
          data: growthValues.map((value, index) => ({
            value: value,
            itemStyle: {
              color: colors[index],
              borderRadius: value >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4]
            }
          })),
          barWidth: '60%',
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          label: {
            show: false
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs text-center px-2">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  const plottableWeekly = data.filter((item) => item.weekly_growth !== 0);
  if (plottableWeekly.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs text-center px-3 max-w-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent p-2">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default WeeklyGrowthChart;
